import React, { createContext, useState, useContext, useEffect, useMemo, useRef, ReactNode } from 'react';
import { StellarWalletsKit, WalletNetwork, allowAllModules, FREIGHTER_ID } from '@creit.tech/stellar-wallets-kit';
import { FlashOnStellarClientV2, getBalances as getBalancesWallet } from '@flashbacktech/flashbackclient/stellarv2';
import { AccountResponse } from '@stellar/stellar-sdk/lib/horizon';
import { useCurrentAccount, useSignAndExecuteTransaction, useSuiClient, useConnectWallet, useDisconnectWallet, useWallets, useCurrentWallet } from '@mysten/dapp-kit';
import { SuiClient, getFullnodeUrl } from '@mysten/sui/client';

import { toastError } from '../../utils/toast';

// Wallet type enum
export enum WalletType {
    STELLAR = 'stellar',
    SUI = 'sui',
}

// Unified wallet context type
interface UnifiedWeb3WalletContextType {
    // Common properties
    walletType: WalletType;
    walletAddress: string | null;
    isConnected: boolean;
    loading: boolean;

    // Stellar-specific
    stellarKit: StellarWalletsKit | null;
    stellarClient: FlashOnStellarClientV2 | null;
    stellarNetwork: any | null;

    // Sui-specific
    suiClient: SuiClient | null;
    suiNetwork: string | null;

    // Common methods
    connectWallet: (type: WalletType) => Promise<{ success: boolean; address?: string; error?: string }>;
    disconnectWallet: () => { success: boolean };
    switchWalletType: (type: WalletType) => void;
    formatPublicKey: (publicKey: string) => string;

    // Stellar methods
    stellarSignTransaction: (xdr: string) => Promise<{ success: boolean; signedTxXdr?: string; error?: string }>;
    getBalances: (address: string) => Promise<AccountResponse['balances']>;

    // Sui methods
    suiSignAndExecute: (transaction: any) => Promise<any>;
}

const UnifiedWeb3WalletContext = createContext<UnifiedWeb3WalletContextType | null>(null);

export const useUnifiedWeb3Wallet = () => {
    const context = useContext(UnifiedWeb3WalletContext);
    if (!context) {
        throw new Error('useUnifiedWeb3Wallet must be used within a UnifiedWeb3WalletProvider');
    }
    return context;
};

interface UnifiedWeb3WalletProviderProps {
    children: ReactNode;
    defaultWalletType?: WalletType;
}

export const UnifiedWeb3WalletProvider: React.FC<UnifiedWeb3WalletProviderProps> = ({
    children,
    defaultWalletType = WalletType.STELLAR // Default to Stellar for backward compatibility
}) => {
    // Wallet type state
    const [walletType, setWalletType] = useState<WalletType>(() => {
        const saved = localStorage.getItem('unified_wallet_type');
        return (saved as WalletType) || defaultWalletType;
    });

    // Common state
    const [loading, setLoading] = useState(true);

    // Stellar state
    const [stellarKit, setStellarKit] = useState<StellarWalletsKit | null>(null);
    const [stellarAddress, setStellarAddress] = useState<string | null>(() => {
        return localStorage.getItem('stellar_wallet_address');
    });
    const [stellarConnected, setStellarConnected] = useState<boolean>(() => {
        return localStorage.getItem('stellar_wallet_connected') === 'true';
    });
    const [stellarSelectedWallet, setStellarSelectedWallet] = useState<string | null>(() => {
        return localStorage.getItem('stellar_selected_wallet');
    });
    const [stellarClient, setStellarClient] = useState<FlashOnStellarClientV2 | null>(null);
    const [stellarNetwork, setStellarNetwork] = useState<any | null>(() => {
        const savedNetwork = localStorage.getItem('stellar_network');
        return savedNetwork ? JSON.parse(savedNetwork) : null;
    });

    // Sui hooks
    const suiCurrentAccount = useCurrentAccount();
    const { currentWallet } = useCurrentWallet();
    const suiClient = useSuiClient();
    const suiWallets = useWallets();
    const { mutate: suiConnect } = useConnectWallet();
    const { mutate: suiDisconnect } = useDisconnectWallet();
    const { mutate: suiSignAndExecute } = useSignAndExecuteTransaction();
    
    // Debug: Log wallet state to understand what dapp-kit is seeing
    useEffect(() => {
        if (walletType === WalletType.SUI) {
            // Check if any wallet has accounts (even if not "connected" in dapp-kit terms)
            const walletsWithAccounts = suiWallets.map(w => ({
                name: w.name,
                accounts: w.accounts?.map(a => a.address) || [],
                // Check if wallet has the 'standard:connect' feature which might have stored connection
                hasConnectFeature: !!w.features?.['standard:connect'],
            }));
            
            console.log('[UnifiedWallet] SUI Wallet Debug:', {
                currentAccount: suiCurrentAccount?.address || 'null',
                currentWallet: currentWallet?.name || 'null',
                currentWalletAccounts: currentWallet?.accounts?.map(a => a.address) || [],
                allWallets: walletsWithAccounts,
            });
            
            // If we have wallets but no current account, check if any wallet has accounts
            // This might indicate the wallet extension has the connection stored but dapp-kit hasn't restored it
            if (!suiCurrentAccount && suiWallets.length > 0) {
                for (const wallet of suiWallets) {
                    // Try to access accounts directly from the wallet
                    // Some wallets might have accounts stored even if not "connected"
                    if (wallet.accounts && wallet.accounts.length > 0) {
                        console.log('[UnifiedWallet] Found wallet with accounts:', wallet.name, wallet.accounts.map(a => a.address));
                    }
                }
            }
        }
    }, [walletType, suiCurrentAccount, currentWallet, suiWallets]);

    // Get network from multiple sources, prioritizing the client's actual configuration
    // The wallet's chain info might be incorrect, so we also check the client URL
    const getNetworkFromClient = (client: SuiClient | null): string | null => {
        if (!client) return null;
        try {
            // Try to infer network from client URL
            // Access transport URL safely - different transport types have different structures
            const transport = (client as any).transport;
            let url = '';
            
            // Try different ways to access the URL
            if (transport?.url) {
                url = transport.url;
            } else if (transport?.request?.url) {
                url = transport.request.url;
            } else if (transport?.client?.url) {
                url = transport.client.url;
            }
            
            // Check URL for network indicators
            if (url) {
                const urlLower = url.toLowerCase();
                if (urlLower.includes('testnet')) return 'testnet';
                if (urlLower.includes('mainnet')) return 'mainnet';
                if (urlLower.includes('devnet')) return 'devnet';
                if (urlLower.includes('localnet')) return 'localnet';
            }
        } catch (error) {
            console.warn('Failed to extract network from client URL:', error);
        }
        return null;
    };
    
    // Try to get network from client first (most reliable)
    const networkFromClient = getNetworkFromClient(suiClient);
    
    // Fallback to wallet/account chain info
    const suiNetwork = 
        suiCurrentAccount?.chains?.[0] || 
        currentWallet?.chains?.[0] || 
        currentWallet?.accounts?.[0]?.chains?.[0] ||
        null;
    
    // Parse the network name from the chain identifier (e.g., "sui:testnet" -> "testnet")
    const getNetworkName = (chain: string | undefined | null): string | null => {
        if (!chain) return null;
        // Chain format is "sui:network" or just "network"
        const parts = chain.split(':');
        return parts.length > 1 ? parts[1] : parts[0];
    };
    
    // Prefer network from client, fallback to parsed chain
    const suiNetworkName = networkFromClient || getNetworkName(suiNetwork);

    // Create a client with the correct network URL if the detected network doesn't match
    // This ensures we use the official network endpoints, not custom localhost ones
    const correctSuiClient = useMemo((): SuiClient | null => {
        if (!suiNetworkName) return suiClient;
        
        // If we have a valid network name, create a client with the official endpoint
        const networkMap: Record<string, string> = {
            testnet: getFullnodeUrl('testnet'),
            mainnet: getFullnodeUrl('mainnet'),
            devnet: getFullnodeUrl('devnet'),
            localnet: getFullnodeUrl('localnet'),
        };
        
        const networkUrl = networkMap[suiNetworkName];
        if (networkUrl) {
            // Check if the current client is using the wrong URL (localhost)
            try {
                const currentUrl = (suiClient as any)?.transport?.url || '';
                if (currentUrl.includes('127.0.0.1') || currentUrl.includes('localhost') || !currentUrl.includes(networkUrl.split('//')[1]?.split('/')[0])) {
                    // Create a new client with the correct network URL
                    console.log(`Creating SUI client for network: ${suiNetworkName} with URL: ${networkUrl}`);
                    return new SuiClient({ url: networkUrl });
                }
            } catch (error) {
                // If we can't check the URL, create a new client anyway to be safe
                console.log(`Creating SUI client for network: ${suiNetworkName} with URL: ${networkUrl} (fallback)`);
                return new SuiClient({ url: networkUrl });
            }
        }
        
        return suiClient;
    }, [suiClient, suiNetworkName]);

    // Derived state - use the appropriate wallet based on selected type
    // For SUI, also check if any wallet has accounts (wallet might be available but not "connected" in dapp-kit terms)
    const suiWalletAddress = useMemo(() => {
        if (suiCurrentAccount?.address) {
            return suiCurrentAccount.address;
        }
        // Fallback: check if currentWallet has accounts
        if (currentWallet?.accounts && currentWallet.accounts.length > 0) {
            return currentWallet.accounts[0].address;
        }
        // Fallback: check if any wallet in suiWallets has accounts
        for (const wallet of suiWallets) {
            if (wallet.accounts && wallet.accounts.length > 0) {
                return wallet.accounts[0].address;
            }
        }
        return null;
    }, [suiCurrentAccount, currentWallet, suiWallets]);
    
    const walletAddress = walletType === WalletType.STELLAR ? stellarAddress : suiWalletAddress;
    
    // For SUI, check if we have a current account - this is the source of truth from dapp-kit
    // The account is automatically restored by dapp-kit, but it may take a moment
    // Use useMemo to ensure this is reactive to suiCurrentAccount changes
    // Also check if any wallet has accounts as a fallback
    const isConnected = useMemo(() => {
        if (walletType === WalletType.STELLAR) {
            return stellarConnected;
        } else {
            // For SUI, check if we have a current account (primary check)
            if (suiCurrentAccount?.address) {
                return true;
            }
            // Fallback: check if currentWallet has accounts
            if (currentWallet?.accounts && currentWallet.accounts.length > 0) {
                return true;
            }
            // Fallback: check if any wallet has accounts (wallet extension might have connection stored)
            for (const wallet of suiWallets) {
                if (wallet.accounts && wallet.accounts.length > 0) {
                    return true;
                }
            }
            return false;
        }
    }, [walletType, stellarConnected, suiCurrentAccount, currentWallet, suiWallets]);
    
    // Debug logging for SUI connection state
    useEffect(() => {
        if (walletType === WalletType.SUI) {
            console.log('[UnifiedWallet] SUI Connection State:', {
                hasAccount: !!suiCurrentAccount,
                address: suiCurrentAccount?.address || 'none',
                isConnected,
                loading,
                walletType,
            });
        }
    }, [walletType, suiCurrentAccount, isConnected, loading]);

    // Refs for SUI wallet connection monitoring
    // These need to be defined outside effects so they persist across re-renders
    const hasCheckedInitialConnection = useRef(false);
    const connectionCheckTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const initialConnectionTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Initialize Stellar kit on mount
    useEffect(() => {
        if (walletType === WalletType.STELLAR) {
            const initializeStellarKit = () => {
                try {
                    const network = process.env.REACT_APP_STELLAR_NETWORK === 'PUBLIC' ? WalletNetwork.PUBLIC : WalletNetwork.TESTNET;
                    console.log('>>>Stellar Network:', network);
                    const walletKit = new StellarWalletsKit({
                        network,
                        selectedWalletId: FREIGHTER_ID,
                        modules: allowAllModules(),
                    });
                    console.log('>>>Stellar Wallet Kit initialized:', network);
                    setStellarKit(walletKit);

                    if (stellarConnected && stellarSelectedWallet && stellarAddress) {
                        console.log('[UnifiedWallet] Attempting to restore Stellar wallet connection...');
                        restoreStellarConnection(walletKit);
                    } else {
                        setLoading(false);
                    }
                } catch (error) {
                    console.error('[UnifiedWallet] Error initializing Stellar Wallet Kit:', error);
                    setLoading(false);
                }
            };

            initializeStellarKit();
        } else {
            // For Sui, we rely on the hooks from dapp-kit
            // The connection state is automatically managed by dapp-kit
            // The wallet extension should restore connection automatically
            // We need to wait for the wallet extension to restore connection
            // dapp-kit persists connections automatically, but it takes a moment to restore
            const checkConnection = () => {
                // Check if we have a current account (primary check)
                if (suiCurrentAccount?.address) {
                    console.log('[UnifiedWallet] SUI wallet connection detected on mount:', suiCurrentAccount.address);
                    // Mark that we've detected a connection
                    hasCheckedInitialConnection.current = true;
                    setLoading(false);
                    return true;
                }
                
                // Also check if currentWallet has accounts (sometimes useCurrentAccount doesn't update immediately)
                if (currentWallet?.accounts && currentWallet.accounts.length > 0) {
                    const firstAccount = currentWallet.accounts[0];
                    console.log('[UnifiedWallet] SUI wallet connection detected via currentWallet:', firstAccount.address);
                    // Mark that we've detected a connection
                    hasCheckedInitialConnection.current = true;
                    setLoading(false);
                    return true;
                }
                
                // Check if any wallet in suiWallets has accounts (wallet might be available but not connected)
                // This can happen when the wallet extension has the connection stored but dapp-kit hasn't restored it yet
                // If we find a wallet with accounts, we can consider it "connected" for UI purposes
                // even though dapp-kit hasn't fully restored the connection yet
                for (const wallet of suiWallets) {
                    if (wallet.accounts && wallet.accounts.length > 0) {
                        const firstAccount = wallet.accounts[0];
                        console.log('[UnifiedWallet] SUI wallet found with accounts:', wallet.name, firstAccount.address);
                        // If this wallet has accounts, it means the wallet extension has the connection stored
                        // We'll treat this as connected for UI purposes, even if useCurrentAccount hasn't updated yet
                        // The connection should restore soon, and our isConnected logic will pick it up
                        hasCheckedInitialConnection.current = true;
                        // Don't set loading to false yet - wait for useCurrentAccount to update
                        // But log that we found a wallet with accounts
                        return false; // Still return false to keep checking, but we know a wallet is available
                    }
                }
                
                // If we have wallets but no accounts, the wallet extension might need time to restore
                // Or the connection might not be stored. Check if we should wait longer or try to reconnect
                if (suiWallets.length > 0 && !suiCurrentAccount) {
                    console.log('[UnifiedWallet] Wallet detected but no accounts yet. Waiting for wallet extension to restore...');
                }
                
                return false;
            };

            // Check immediately
            if (checkConnection()) {
                return;
            }

            // If not connected, wait a bit for wallet extension to restore
            // dapp-kit stores connection in localStorage and restores it automatically
            // Use a longer timeout to give the wallet extension more time to restore
            // Also check multiple times to catch when the connection is restored
            // Increase the timeout since wallet extensions can take time to restore
            let checkCount = 0;
            const maxChecks = 12; // Check 12 times over 6 seconds (every 500ms)
            const checkInterval = 500;
            
            const timer = setInterval(() => {
                checkCount++;
                if (checkConnection()) {
                    console.log('[UnifiedWallet] SUI wallet connection restored after', checkCount * checkInterval, 'ms');
                    clearInterval(timer);
                    setLoading(false);
                } else if (checkCount >= maxChecks) {
                    console.log('[UnifiedWallet] SUI wallet connection check complete - no connection found after', maxChecks * checkInterval, 'ms');
                    console.log('[UnifiedWallet] Available wallets:', suiWallets.map(w => w.name));
                    
                    // If we have wallets available but no connection, try to auto-connect to the first available wallet
                    // This helps restore connections that the wallet extension has stored but dapp-kit hasn't restored
                    if (suiWallets.length > 0 && !suiCurrentAccount) {
                        const firstWallet = suiWallets[0];
                        console.log('[UnifiedWallet] Attempting to auto-connect to wallet:', firstWallet.name);
                        
                        // Try to connect to the first available wallet
                        // This will trigger the wallet extension to restore the connection if it has one stored
                        try {
                            suiConnect(
                                { wallet: firstWallet },
                                {
                                    onSuccess: () => {
                                        console.log('[UnifiedWallet] Auto-connect successful');
                                    },
                                    onError: (error) => {
                                        console.log('[UnifiedWallet] Auto-connect failed (this is normal if wallet requires user approval):', error);
                                    },
                                }
                            );
                        } catch (error) {
                            console.log('[UnifiedWallet] Error attempting auto-connect:', error);
                        }
                    }
                    
                    clearInterval(timer);
                    setLoading(false);
                }
            }, checkInterval);
            
            return () => clearInterval(timer);
        }
    }, [walletType, stellarConnected, stellarSelectedWallet, stellarAddress, suiCurrentAccount, hasCheckedInitialConnection]);

    // Monitor SUI wallet connection changes continuously
    // This ensures we detect when the wallet extension restores the connection

    // Set a flag after initial mount period to indicate we've given wallet time to restore
    useEffect(() => {
        if (walletType === WalletType.SUI) {
            // Give wallet extension time to restore connection on page load
            initialConnectionTimeoutRef.current = setTimeout(() => {
                hasCheckedInitialConnection.current = true;
                console.log('[UnifiedWallet] Initial connection check period complete');
            }, 3000); // 3 seconds to allow wallet extension to restore

            return () => {
                if (initialConnectionTimeoutRef.current) {
                    clearTimeout(initialConnectionTimeoutRef.current);
                    initialConnectionTimeoutRef.current = null;
                }
            };
        }
    }, [walletType]);

    useEffect(() => {
        if (walletType === WalletType.SUI) {
            if (suiCurrentAccount?.address) {
                console.log('[UnifiedWallet] SUI wallet connected:', suiCurrentAccount.address);
                // Clear any pending disconnection check
                if (connectionCheckTimeoutRef.current) {
                    clearTimeout(connectionCheckTimeoutRef.current);
                    connectionCheckTimeoutRef.current = null;
                }
                // Mark that we've seen a connection (even if before initial check period)
                hasCheckedInitialConnection.current = true;
                // If we were loading and now we have a connection, stop loading
                // Force a state update to ensure components re-render with the new connection state
                if (loading) {
                    setLoading(false);
                }
                // Log connection state for debugging
                console.log('[UnifiedWallet] SUI wallet connection state updated - isConnected should be true');
            } else {
                // Don't immediately log as disconnected - wait a bit to see if wallet is restoring
                // Only log as disconnected if we've already checked initial connection
                if (hasCheckedInitialConnection.current) {
                    // Clear any existing timeout
                    if (connectionCheckTimeoutRef.current) {
                        clearTimeout(connectionCheckTimeoutRef.current);
                    }
                    // Wait a bit before logging as disconnected (wallet might be restoring)
                    connectionCheckTimeoutRef.current = setTimeout(() => {
                        // Double-check if still disconnected after timeout
                        if (!suiCurrentAccount?.address) {
                            console.log('[UnifiedWallet] SUI wallet disconnected');
                        }
                        connectionCheckTimeoutRef.current = null;
                    }, 1000);
                } else {
                    // Still in initial check period - don't log as disconnected yet
                    console.log('[UnifiedWallet] SUI wallet not yet connected, waiting for restoration...');
                }
            }
        }

        return () => {
            if (connectionCheckTimeoutRef.current) {
                clearTimeout(connectionCheckTimeoutRef.current);
                connectionCheckTimeoutRef.current = null;
            }
        };
    }, [walletType, suiCurrentAccount, loading]);

    // Function to restore Stellar wallet connection
    const restoreStellarConnection = async (walletKit: StellarWalletsKit) => {
        try {
            walletKit.setWallet(stellarSelectedWallet!);

            const { address } = await walletKit.getAddress();
            const currentNetwork = await walletKit.getNetwork();

            const expectedNetwork = process.env.REACT_APP_STELLAR_NETWORK === 'PUBLIC' 
              ? WalletNetwork.PUBLIC 
              : WalletNetwork.TESTNET;

            const currentNetworkStr = typeof currentNetwork === 'string' 
              ? currentNetwork 
              : (currentNetwork as any).network;

            console.log('[UnifiedWallet] restore - wallet network:', currentNetwork, 'expected network:', expectedNetwork);

            // Freighter returns passphrases like "Test SDF Network ; September 2015" or "Public Global Stellar Network ; September 2015"
            const isTestnet = currentNetworkStr.toLowerCase().includes('test');
            const isPublic = currentNetworkStr.toLowerCase().includes('public');
            const isExpectedTestnet = expectedNetwork === WalletNetwork.TESTNET;
            
            const networkMatches = (isTestnet && isExpectedTestnet) || (isPublic && !isExpectedTestnet);

            if (!networkMatches) {
              toastError(`Please switch your Stellar wallet network to ${expectedNetwork} to continue.`);
              console.log(`[UnifiedWallet] Wallet network mismatch. Expected: ${expectedNetwork}, Got: ${currentNetworkStr}`);
              clearStellarStoredData();
              setLoading(false);
              return;
            }

            if (address === stellarAddress) {
                const contractAddress = process.env.REACT_APP_CONTRACT_ADDRESS || '';

                const newClient = new FlashOnStellarClientV2({
                    contractAddress,
                    network: currentNetwork,
                    signTransaction: async (xdrToSign) => {
                        const signedTx = await walletKit.signTransaction(xdrToSign);
                        return signedTx.signedTxXdr;
                    },
                });

                setStellarClient(newClient);
                setStellarNetwork(currentNetwork);
                console.log('[UnifiedWallet] Stellar wallet connection restored successfully');
            } else {
                clearStellarStoredData();
            }
        } catch (error) {
            console.error('[UnifiedWallet] Error restoring Stellar wallet:', error);
            clearStellarStoredData();
        } finally {
            setLoading(false);
        }
    };

    // Function to clear Stellar stored wallet data
    const clearStellarStoredData = () => {
        localStorage.removeItem('stellar_wallet_address');
        localStorage.removeItem('stellar_wallet_connected');
        localStorage.removeItem('stellar_selected_wallet');
        localStorage.removeItem('stellar_network');

        setStellarAddress(null);
        setStellarConnected(false);
        setStellarSelectedWallet(null);
        setStellarClient(null);
        setStellarNetwork(null);
    };

    // Function to store Stellar wallet data
    const storeStellarData = (address: string, walletId: string, network: any) => {
        localStorage.setItem('stellar_wallet_address', address);
        localStorage.setItem('stellar_wallet_connected', 'true');
        localStorage.setItem('stellar_selected_wallet', walletId);
        localStorage.setItem('stellar_network', JSON.stringify(network));
        // Notify Web3WalletContext so StellarBuckets and other legacy consumers see connected state
        window.dispatchEvent(new CustomEvent('stellarWalletStateChanged'));
    };

    // Sync Stellar state from localStorage when Web3WalletContext (or another tab) connects/disconnects
    useEffect(() => {
        const syncFromLocalStorage = () => {
            if (walletType !== WalletType.STELLAR) return;
            const address = localStorage.getItem('stellar_wallet_address');
            const connected = localStorage.getItem('stellar_wallet_connected') === 'true';
            const selected = localStorage.getItem('stellar_selected_wallet');
            const networkRaw = localStorage.getItem('stellar_network');
            const network = networkRaw ? (() => { try { return JSON.parse(networkRaw); } catch { return null; } })() : null;
            setStellarAddress(address);
            setStellarConnected(connected);
            setStellarSelectedWallet(selected);
            if (!connected || !address) {
                setStellarClient(null);
                setStellarNetwork(null);
            } else {
                setStellarNetwork(network);
                // Client will be restored by the init effect when stellarConnected/stellarAddress/stellarSelectedWallet change
            }
        };
        window.addEventListener('stellarWalletStateChanged', syncFromLocalStorage);
        return () => window.removeEventListener('stellarWalletStateChanged', syncFromLocalStorage);
    }, [walletType]);

    // Unified connect wallet function
    const connectWallet = async (type: WalletType): Promise<{ success: boolean; address?: string; error?: string }> => {
        if (type === WalletType.STELLAR) {
            return connectStellarWallet();
        } else {
            return connectSuiWallet();
        }
    };

    // Connect Stellar wallet
    const connectStellarWallet = async (): Promise<{ success: boolean; address?: string; error?: string }> => {
        if (!stellarKit) {
            return { success: false, error: 'Stellar Kit not initialized' };
        }

        try {
            const walletSelectionPromise = new Promise<{ success: boolean; address?: string; error?: string }>((resolve) => {
                stellarKit.openModal({
                    onWalletSelected: async (option) => {
                        try {
                            stellarKit.setWallet(option.id);
                            setStellarSelectedWallet(option.id);

                            const { address } = await stellarKit.getAddress();
                            const currentNetwork = await stellarKit.getNetwork();

                            const expectedNetwork = process.env.REACT_APP_STELLAR_NETWORK === 'PUBLIC' 
                              ? WalletNetwork.PUBLIC 
                              : WalletNetwork.TESTNET;

                            const currentNetworkStr = typeof currentNetwork === 'string' 
                              ? currentNetwork 
                              : (currentNetwork as any).network;

                            console.log('[UnifiedWallet] connect - wallet network:', currentNetwork, 'expected network:', expectedNetwork);

                            // Freighter returns passphrases like "Test SDF Network ; September 2015" or "Public Global Stellar Network ; September 2015"
                            const isTestnet = currentNetworkStr.toLowerCase().includes('test');
                            const isPublic = currentNetworkStr.toLowerCase().includes('public');
                            const isExpectedTestnet = expectedNetwork === WalletNetwork.TESTNET;
                            
                            const networkMatches = (isTestnet && isExpectedTestnet) || (isPublic && !isExpectedTestnet);

                            if (!networkMatches) {
                              toastError(`Please switch your Stellar wallet network to ${expectedNetwork} to continue.`);
                              resolve({ success: false, error: `Wrong network. Please switch to ${expectedNetwork}` });
                              return;
                            }

                            const contractAddress = process.env.REACT_APP_CONTRACT_ADDRESS || '';
                            const newClient = new FlashOnStellarClientV2({
                                contractAddress,
                                network: currentNetwork,
                                signTransaction: async (xdrToSign) => {
                                    const signedTx = await stellarKit.signTransaction(xdrToSign);
                                    return signedTx.signedTxXdr;
                                },
                            });

                            setStellarClient(newClient);
                            setStellarNetwork(currentNetwork);
                            setStellarAddress(address);
                            setStellarConnected(true);
                            storeStellarData(address, option.id, currentNetwork);

                            console.log('[UnifiedWallet] Stellar wallet connected:', address);
                            resolve({ success: true, address });
                        } catch (error) {
                            console.error('[UnifiedWallet] Error connecting Stellar wallet:', error);
                            resolve({ success: false, error: error instanceof Error ? error.message : 'Unknown error' });
                        }
                    },
                    onClosed: (err) => {
                        if (err) {
                            resolve({ success: false, error: 'Modal closed with error' });
                        } else {
                            resolve({ success: false, error: 'Modal closed without wallet selection' });
                        }
                    }
                });
            });

            return await walletSelectionPromise;
        } catch (error) {
            console.error('[UnifiedWallet] Error opening Stellar wallet modal:', error);
            return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
        }
    };

    // Connect Sui wallet
    const connectSuiWallet = async (): Promise<{ success: boolean; address?: string; error?: string }> => {
        return new Promise((resolve) => {
            // Get available wallets (useWallets returns an array directly)
            const availableWallets = suiWallets;
            
            if (availableWallets.length === 0) {
                resolve({ success: false, error: 'No Sui wallets found. Please install a Sui wallet extension.' });
                return;
            }
            
            // If only one wallet, connect to it directly
            // Otherwise, the WalletProvider should show a modal
            // For now, try connecting to the first available wallet
            const walletToConnect = availableWallets[0];
            
            if (!walletToConnect) {
                resolve({ success: false, error: 'No wallet available to connect' });
                return;
            }
            
            // Connect to the wallet
            suiConnect(
                { wallet: walletToConnect },
                {
                    onSuccess: () => {
                        // Wait a bit for the account to be updated
                        setTimeout(() => {
                            const address = suiCurrentAccount?.address;
                            console.log('[UnifiedWallet] Sui wallet connected:', address);
                            resolve({ success: true, address });
                        }, 100);
                    },
                    onError: (error) => {
                        console.error('[UnifiedWallet] Error connecting Sui wallet:', error);
                        resolve({ success: false, error: error?.message || 'Failed to connect Sui wallet' });
                    },
                }
            );
        });
    };

    // Unified disconnect wallet
    const disconnectWallet = () => {
        if (walletType === WalletType.STELLAR) {
            clearStellarStoredData();
        } else {
            suiDisconnect();
        }
        return { success: true };
    };

    // Switch wallet type
    const switchWalletType = (type: WalletType) => {
        // Disconnect current wallet before switching
        disconnectWallet();

        setWalletType(type);
        localStorage.setItem('unified_wallet_type', type);
        console.log('[UnifiedWallet] Switched wallet type to:', type);
    };

    // Stellar-specific: Sign transaction
    const stellarSignTransaction = async (xdr: string): Promise<{ success: boolean; signedTxXdr?: string; error?: string }> => {
        if (!stellarKit || !stellarConnected) {
            return { success: false, error: 'Stellar wallet not connected' };
        }

        try {
            const { signedTxXdr } = await stellarKit.signTransaction(xdr);
            return { success: true, signedTxXdr };
        } catch (error) {
            console.error('[UnifiedWallet] Error signing Stellar transaction:', error);
            return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
        }
    };

    // Stellar-specific: Get balances
    const getBalances = async (address: string): Promise<AccountResponse['balances']> => {
        if (walletType !== WalletType.STELLAR || !stellarNetwork) {
            throw new Error('Stellar wallet not connected');
        }
        return await getBalancesWallet(address, stellarNetwork.network);
    };

    // Sui-specific: Sign and execute transaction
    const suiSignAndExecuteWrapper = async (transaction: any): Promise<any> => {
        if (walletType !== WalletType.SUI || !suiCurrentAccount) {
            throw new Error('Sui wallet not connected');
        }

        return new Promise((resolve, reject) => {
            suiSignAndExecute(
                { transaction },
                {
                    onSuccess: (result) => resolve(result),
                    onError: (error) => reject(error),
                }
            );
        });
    };

    // Format public key for display
    const formatPublicKey = (publicKey: string): string => {
        if (!publicKey) return '';
        return `${publicKey.substring(0, 4)}...${publicKey.substring(publicKey.length - 4)}`;
    };

    // Debug logging (only log when network changes or on initial connection)
    useEffect(() => {
        if (walletType === WalletType.SUI && isConnected) {
            console.log('SUI Network Debug:', {
                currentWallet: currentWallet?.name,
                walletChains: currentWallet?.chains,
                accountChains: suiCurrentAccount?.chains,
                accountAddress: suiCurrentAccount?.address,
                networkFromClient,
                finalNetwork: suiNetworkName,
                rawChain: suiNetwork,
                clientUrl: suiClient ? 'present' : 'null',
            });
        }
    }, [walletType, isConnected, suiNetworkName, currentWallet?.name, suiCurrentAccount?.address]);
    
    const value: UnifiedWeb3WalletContextType = {
        walletType,
        walletAddress,
        isConnected,
        loading,
        stellarKit,
        stellarClient,
        stellarNetwork,
        suiClient: correctSuiClient,
        suiNetwork: suiNetworkName,
        connectWallet,
        disconnectWallet,
        switchWalletType,
        formatPublicKey,
        stellarSignTransaction,
        getBalances,
        suiSignAndExecute: suiSignAndExecuteWrapper,
    };

    return (
        <UnifiedWeb3WalletContext.Provider value={value}>
            {children}
        </UnifiedWeb3WalletContext.Provider>
    );
};

export default UnifiedWeb3WalletContext;
